import { BundleDetails } from '@/types/bundles.types';
import { useTranslation } from 'react-i18next';
import { FreezeFormErrors, FreezeFormValues } from '../CreateFreeze/CreateFreeze.types';
import Card from '@/components/Card/Card';
import styles from './Steps.module.css';
import { renderOccasions } from '@/utils/formatting/formatBundleProperties';
import { ChangeEvent, useEffect, useState } from 'react';
import RadioGroup from '@/components/RadioGroup/RadioGroup';
import { Container, Grid } from '@mui/material';
import { NationalProgramStates } from '@/types/nationalProgram.types';
import Dropdown from '@/components/Dropdown/Dropdown';

function nothingChanges(editing: boolean | undefined, initialFormData: FreezeFormValues, formValues: FreezeFormValues) {
  if (
    initialFormData.defaultPrice === formValues.defaultPrice &&
    formValues.defaultPrice === undefined &&
    initialFormData.optOutHidden === formValues.optOutHidden &&
    formValues.optOutHidden === undefined &&
    initialFormData.redeemableOnline === formValues.redeemableOnline &&
    formValues.redeemableOnline === undefined &&
    initialFormData.visibleOnline === formValues.visibleOnline &&
    formValues.visibleOnline === undefined
  ) {
    return true;
  }
  return false;
}

export const validateDuplicatePrices = (
  formValuesList: any,
  errorMessagesList: any,
  duplicateText: string,
): boolean => {
  let hasError = false;

  const priceFields = ['defaultPrice', 'nationalPricePointTwo', 'nationalPricePointThree', 'nationalPricePointFour'];

  priceFields.forEach((field) => {
    if (formValuesList[field]) {
      priceFields.forEach((otherField) => {
        if (field !== otherField && formValuesList[field] === formValuesList[otherField]) {
          // eslint-disable-next-line no-param-reassign
          errorMessagesList[field] = duplicateText;
          hasError = true;
        }
      });
    }
  });

  return hasError;
};

export const FreezeStepTwo = ({
  bundle,
  state,
  editing,
  initialFormData,
  onChangeFreezeData,
}: {
  bundle: BundleDetails | undefined;
  editing?: boolean;
  state?: NationalProgramStates;
  initialFormData: FreezeFormValues;
  onChangeFreezeData: (data: FreezeFormValues, valid: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<FreezeFormValues>(initialFormData);
  const [formErrors, setFormErrors] = useState<FreezeFormErrors>({} as FreezeFormErrors);
  const occasion = renderOccasions(t, bundle?.occasions);
  const recommendedPrices = bundle?.recommendedPrices?.map((price) => price?.toFixed(2));

  const priceOptions = recommendedPrices?.map((price: string) => ({
    value: Number(price).toFixed(2) || '',
    label: `$${Number(price).toFixed(2)}` || '',
  }));
  const defaultDropdownPriceOptions = [{ value: '', label: t('common.select') }, ...(priceOptions || [])];
  const duplicateRecPriceText = t('createFreezeModal.stepTwo.pricePoints.priceAlreadySelected');

  const isValid = () => {
    let error = false;
    const errorMessages = {
      freezeName: '',
      optInStartDate: '',
      freezeStartDate: '',
      freezeEndDate: '',
      defaultPrice: '',
      nationalPricePointTwo: '',
      nationalPricePointThree: '',
      nationalPricePointFour: '',
      optOutHidden: '',
      redeemableOnline: '',
      visibleOnline: '',
    };
    // invalid but without messages
    if (!editing && nothingChanges(editing, initialFormData, formValues)) {
      return false;
    }
    if (!formValues.defaultPrice) {
      errorMessages.defaultPrice = t('createFreezeModal.stepTwo.pricePoints.enterPrice');
      error = true;
    }
    if (formValues.optOutHidden === undefined) {
      error = true;
    }
    if (formValues.redeemableOnline === undefined) {
      error = true;
    }
    if (formValues.visibleOnline === undefined) {
      error = true;
    }

    if (validateDuplicatePrices(formValues, errorMessages, duplicateRecPriceText)) {
      error = true;
    }

    setFormErrors(errorMessages);
    return !error && !!formValues.defaultPrice;
  };

  const handleDropdownSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const eventTargetId = event.target.id;
    const eventTargetValue = event.target.value;

    setFormValues({
      ...formValues,
      [eventTargetId]: !eventTargetValue ? '' : eventTargetValue,
    });
  };

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, optOutHidden: event.target.id === 'radioHide' });
  };

  const onRedeemableOnlineRadioChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.target.id === 'redeemable_yes') {
      setFormValues({ ...formValues, redeemableOnline: true });
    } else {
      setFormValues({ ...formValues, redeemableOnline: false, visibleOnline: false });
    }
  };

  const onVisibleOnlineRadioChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, visibleOnline: event.target.id === 'visible_yes' });
  };

  useEffect(() => {
    const formValid = isValid();
    onChangeFreezeData(formValues, formValid);
  }, [formValues]);

  return (
    <Container fixed>
      <Grid container direction="column" justifyContent="center" alignItems="center" spacing={4}>
        <Grid item mt={6}>
          <Card className={styles.fixedWith}>
            <div className={styles.row}>
              <div className={styles.column}>
                <p>{t('createFreezeModal.stepTwo.review.displayName')}</p>
                <p>{t('createFreezeModal.stepTwo.review.bundleCode')}</p>
                <p>{t('createFreezeModal.stepTwo.review.bundleDescription')}</p>
                <p>{t('createFreezeModal.stepTwo.review.occasion')}</p>
              </div>
              <div className={styles.columnDescriptions}>
                <p>{bundle?.name}</p>
                <p>{bundle?.code}</p>
                <p>{bundle?.internalName}</p>
                <p>{occasion}</p>
              </div>
            </div>
          </Card>
        </Grid>
        <Grid item>
          <Card>
            <h3 className={styles.cardHeader}>{t('createFreezeModal.stepTwo.pricePoints.header')}</h3>
            <div className={styles.divider} />
            <div className={styles.widgetBody}>
              <h3>{t('createFreezeModal.stepTwo.pricePoints.continentalUsHeader')}</h3>
              <div className={styles.row}>
                <div className={styles.inputContainer}>
                  <Dropdown
                    label={t('createFreezeModal.stepTwo.pricePoints.nationalPricePointLabel')}
                    id="defaultPrice"
                    options={defaultDropdownPriceOptions}
                    hidePlaceholder={false}
                    tooltipText={t('createFreezeModal.stepTwo.pricePoints.nationalPricePointTooltip')}
                    onChange={handleDropdownSelect}
                    errorText={formErrors.defaultPrice}
                    dataTestId="defaultPrice"
                    value={formValues.defaultPrice}
                  />
                </div>
                <div className={styles.inputContainer}>
                  <Dropdown
                    label={t('createFreezeModal.stepTwo.pricePoints.pricePoint2Label')}
                    id="nationalPricePointTwo"
                    optionalLabelText={t('common.optionalLabel')}
                    options={defaultDropdownPriceOptions}
                    redBackground={false}
                    hidePlaceholder={false}
                    onChange={handleDropdownSelect}
                    errorText={formErrors.nationalPricePointTwo}
                    dataTestId="nationalPricePointTwo"
                    value={formValues.nationalPricePointTwo}
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.inputContainer}>
                  <Dropdown
                    label={t('createFreezeModal.stepTwo.pricePoints.pricePoint3Label')}
                    id="nationalPricePointThree"
                    optionalLabelText={t('common.optionalLabel')}
                    options={defaultDropdownPriceOptions}
                    redBackground={false}
                    hidePlaceholder={false}
                    onChange={handleDropdownSelect}
                    errorText={formErrors.nationalPricePointThree}
                    dataTestId="nationalPricePointThree"
                    value={formValues.nationalPricePointThree}
                  />
                </div>
                <div className={styles.inputContainer}>
                  <Dropdown
                    label={t('createFreezeModal.stepTwo.pricePoints.pricePoint4Label')}
                    id="nationalPricePointFour"
                    optionalLabelText={t('common.optionalLabel')}
                    options={defaultDropdownPriceOptions}
                    redBackground={false}
                    hidePlaceholder={false}
                    onChange={handleDropdownSelect}
                    errorText={formErrors.nationalPricePointFour}
                    dataTestId="nationalPricePointFour"
                    value={formValues.nationalPricePointFour}
                  />
                </div>
              </div>
              <Grid container direction="row" spacing={3}>
                <Grid item xs={6}>
                  <div>
                    <h3>{t('createFreezeModal.stepTwo.pricePoints.optOutSettingsHeader')}</h3>
                    <div className={styles.radioGroupContainer}>
                      <RadioGroup
                        label={t('createFreezeModal.stepTwo.pricePoints.optOutSettingsLabel')}
                        options={[
                          { label: t('common.show'), id: 'radioShow' },
                          { label: t('common.hide'), id: 'radioHide' },
                        ]}
                        selected={
                          formValues.optOutHidden === undefined
                            ? ''
                            : formValues.optOutHidden
                            ? 'radioHide'
                            : 'radioShow'
                        }
                        onChange={handleRadioChange}
                        disabled={
                          editing && (state === NationalProgramStates.LIVE || state === NationalProgramStates.OPT_IN)
                        }
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <h3>{t('createFreezeModal.stepTwo.onlineSettings')}</h3>
                    <div className={styles.row}>
                      <div className={styles.radioGroupContainer}>
                        <RadioGroup
                          label={t('createFreezeModal.stepTwo.redeemableOnline')}
                          options={[
                            { label: t('common.yes'), id: 'redeemable_yes' },
                            { label: t('common.no'), id: 'redeemable_no' },
                          ]}
                          selected={
                            formValues.redeemableOnline === undefined
                              ? ''
                              : formValues.redeemableOnline === true
                              ? 'redeemable_yes'
                              : 'redeemable_no'
                          }
                          errorText={formErrors.redeemableOnline}
                          onChange={onRedeemableOnlineRadioChange}
                          disabled={
                            editing && (state === NationalProgramStates.LIVE || state === NationalProgramStates.OPT_IN)
                          }
                        />
                      </div>
                      <div className={styles.radioGroupContainer}>
                        <RadioGroup
                          label={t('createFreezeModal.stepTwo.visibleOnline')}
                          options={[
                            { label: t('common.yes'), id: 'visible_yes' },
                            { label: t('common.no'), id: 'visible_no' },
                          ]}
                          selected={
                            formValues.visibleOnline === undefined
                              ? ''
                              : formValues.visibleOnline
                              ? 'visible_yes'
                              : 'visible_no'
                          }
                          onChange={onVisibleOnlineRadioChange}
                          disabled={
                            !formValues.redeemableOnline ||
                            (editing &&
                              (state === NationalProgramStates.LIVE || state === NationalProgramStates.OPT_IN))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FreezeStepTwo;
